/**
 * Pure helpers for the public slot-model responses (Phase 5c). No network and no client
 * import: pages use them directly, and lib/api/public.js re-exports them, so a test that
 * mocks lib/api/public's calls still gets the real helpers.
 */

/**
 * The machine-readable error code of a failed public call, or null:
 * `detail.error` (FastAPI HTTPException with a dict detail) or a top-level `error`.
 * Any 429 reads as 'rate_limited', whatever the body: slowapi's own body is
 * {"error": "Rate limit exceeded: 5 per 1 hour"}, a sentence, not a code.
 */
export const publicErrorCode = (error) => {
  if (error?.response?.status === 429) return 'rate_limited';
  const data = error?.response?.data;
  const code = (data && data.detail && typeof data.detail === 'object' && data.detail.error) || (data && data.error);
  if (typeof code === 'string' && code) return code;
  return null;
};

/** The body of an axios response, or the value itself (tolerates either call style). */
export const unwrap = (res) => {
  if (res && typeof res === 'object' && !Array.isArray(res) && 'data' in res) return res.data;
  return res;
};

/**
 * GET /venues/availability body → the venue list: a bare array or `{ venues: [...] }`;
 * entries without a `location_id` are dropped.
 */
export const toVenueList = (data) => {
  const list = Array.isArray(data) ? data : Array.isArray(data?.venues) ? data.venues : [];
  return list.filter((v) => v && v.location_id);
};

const trimmed = (v) => (typeof v === 'string' ? v.trim() : '');

/**
 * POST /scan/record body (Phase 6c). Today's ScanNew payload fields (location_id,
 * session_id, referral_source, campaign_id, user_id) plus the coaster's design token `d`
 * (Phase 6b). `hub_id` is accepted as an alias for `location_id` (the scan URL's name for
 * it). referral_source defaults to 'qr_code' with a venue and 'direct' without, as in
 * ScanNew. `d` is sent only when it is a non-empty string (the backend ignores a token
 * that is not 1-64 url-safe characters, so it is passed through as read from the URL).
 * A body already in today's shape passes through unchanged.
 */
export const scanRecordBody = (payload = {}) => {
  const {
    hub_id: hubId, location_id: locationId, session_id: sessionId, d, referral_source: referral,
    campaign_id: campaignId, user_id: userId, ...rest
  } = payload || {};
  const location = locationId !== undefined ? locationId : (hubId || null);
  const body = {
    ...rest,
    location_id: location || null,
    session_id: sessionId,
    referral_source: referral || (location ? 'qr_code' : 'direct'),
    campaign_id: campaignId ?? null,
    user_id: userId ?? null,
  };
  const token = trimmed(d);
  if (token) body.d = token;
  return body;
};

/**
 * POST /businesses/{id}/view body (Phase 6c): only the attribution fields that are set
 * (the backend stores an unknown or foreign id as null; an empty body is valid).
 */
export const businessViewBody = ({ source, location_id: locationId, design_id: designId, page_id: pageId } = {}) => {
  const body = {};
  if (trimmed(source)) body.source = trimmed(source);
  if (trimmed(locationId)) body.location_id = trimmed(locationId);
  if (trimmed(designId)) body.design_id = trimmed(designId);
  if (trimmed(pageId)) body.page_id = trimmed(pageId);
  return body;
};

/** HTTP status of a failed call, or null (transport errors have none). */
export const httpStatus = (error) => {
  const status = error?.response?.status;
  return typeof status === 'number' ? status : null;
};
